import {
  chain,
  filter,
  get,
  isEmpty,
  map,
  min,
  size,
  sortBy,
  sumBy,
  values,
} from 'lodash';
import cx from 'classnames';
import moment from 'moment';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Paper } from '@mui/material';

import styles from '../Deliveries.module.css';
import addWeekDays from '../../../Lib/Utils/Date/add-week-days';
import Button from '../../ReusableComponents/Button/Button';
import DateSwitcher from '../../ReusableComponents/DateSwitcher/DateSwitcher';
import BookingTypes from '../../../Lib/Constants/booking-types';
import LoadingText from '../../ReusableComponents/LoadingText/LoadingText';
import { FeatureFlagsContext } from '../../../Lib/Contexts/FeatureFlagsContext.tsx';

const dateMinutes = (date) =>
  Math.abs(moment.utc(date).startOf('day').diff(moment.utc(date), 'minutes'));

const getDeltaDate = (days) => {
  const startOfToday = moment
    .utc(moment().format('LLLL'), 'LLLL')
    .startOf('day');

  return addWeekDays(startOfToday, days);
};

const getSlotDeliveries = (timeSlot, deliveries) =>
  filter(deliveries, (d) =>
    moment.utc(d.starts_at).isSame(moment.utc(timeSlot)),
  );
const getSlotUnits = (timeSlot, deliveries) =>
  sumBy(getSlotDeliveries(timeSlot, deliveries), 'units') || '0';
const getSlotBoxes = (timeSlot, deliveries) =>
  sumBy(getSlotDeliveries(timeSlot, deliveries), 'boxes') || '0';

const getEndDate = (purchaseOrders, vendorView = false) => {
  if (!vendorView) return null;
  return moment.utc(min(map(purchaseOrders, 'due_date')));
};

class SelectTimeSlot extends Component {
  constructor(props) {
    super(props);
    const { timeSlot } = props.deliveryDetails;
    const date = props.defaultDate;

    this.state = {
      timeSlot,
      date,
    };

    this.onChange = this.onChange.bind(this);
    this.onDateChange = this.onDateChange.bind(this);
    this.getDeliveries = this.getDeliveries.bind(this);
    this.selectTimeSlot = this.selectTimeSlot.bind(this);

    this.getDeliveries(date);
  }

  async componentDidMount() {
    const { location_id, boxes, purchaseOrders, units } =
      this.props.deliveryDetails;
    const date = this.props.defaultDate;

    const options = {
      location_id,
      boxes,
      units,
      date: moment.utc(date).startOf('day').format(),
    };

    options.purchase_order_numbers = map(purchaseOrders, 'po_number');

    this.props.getAvailabilities(options);
  }

  onChange(timeSlot) {
    const { onChange } = this.props;
    this.setState({ timeSlot }, () => {
      onChange(timeSlot);
    });
  }

  onDateChange(date) {
    const {
      deliveryDetails: { location_id, purchaseOrders, boxes, units },
      getAvailabilities,
    } = this.props;
    const utcDate = moment.utc(date).startOf('day').format();

    const options = {
      location_id,
      boxes,
      units,
      date: utcDate,
    };

    options.purchase_order_numbers = map(purchaseOrders, 'po_number');

    this.setState({ date }, () => {
      getAvailabilities(options);
      this.getDeliveries(date);
    });
  }

  getDeliveries(date) {
    const {
      getDeliveries,
      deliveryDetails: { location_id },
    } = this.props;
    const options = {
      location_id,
      type: BookingTypes.ORDER_DELIVERY,
      date: moment.utc(date).startOf('day').format(),
    };
    getDeliveries(options);
  }

  selectTimeSlot(timeSlot) {
    const { onChange } = this.props;
    this.setState({ timeSlot }, () => onChange(timeSlot));
  }

  render() {
    const {
      next,
      back,
      availabilities,
      deliveries,
      deliveryDetails,
      onClose,
      vendorView,
      loading,
      location,
    } = this.props;

    const purchaseOrders = values(get(deliveryDetails, 'purchaseOrders'));
    const { date, timeSlot: ts } = this.state;
    const datesAvailabilities = chain(availabilities)
      .find((a) =>
        moment.utc(a.date, 'MM/DD/YYYY', 'day').isSame(moment.utc(date), 'day'),
      )
      .get('availabilities')
      .value();
    const formattedTS = moment.utc(ts).format('LLLL');

    return (
      <div className={styles.step}>
        <div className={styles.stepContainer}>
          <div className={cx(styles.stepHeader, styles.stepHeaderRow)}>
            <h3>Select A Time Slot:</h3>
            <div className={cx(styles.stepDetails, styles.selectedTimeDesc)}>
              {ts && (
                <div className={cx(styles.stepHeaderDetail)}>
                  <span>Selected Time:</span>
                  <span className={styles.selectedTime}>{formattedTS}</span>
                </div>
              )}
            </div>
          </div>

          <DateSwitcher
            date={moment.utc(date)}
            business={vendorView}
            onDateChange={this.onDateChange}
            start={vendorView ? getDeltaDate(location.min_booking_days) : null}
            end={getEndDate(purchaseOrders, vendorView)}
            hideToday={vendorView}
          />

          {!isEmpty(datesAvailabilities) ? (
            <div className={styles.timeSlots}>
              <div className={styles.timeSlotsHeaders}>
                <div>Select Time</div>
                {!vendorView && <div>Deliveries</div>}
                {!vendorView && <div>Units</div>}
                {!vendorView && <div>Boxes</div>}
              </div>
              <div className={styles.timeSlotsBody}>
                {map(sortBy(datesAvailabilities, dateMinutes), (timeSlot) => {
                  const selectedTimeSlot = moment(timeSlot).isSame(moment(ts));
                  const minutes = dateMinutes(timeSlot);

                  return (
                    <Paper key={timeSlot} className={styles.timeSlotsRow}>
                      <Button
                        onClick={() => this.selectTimeSlot(new Date(timeSlot))}
                        variant={selectedTimeSlot ? 'contained' : 'outlined'}
                        color="primary"
                        small
                        className={cx({
                          [styles.selectedSlot]: selectedTimeSlot,
                        })}
                      >
                        {moment
                          .utc(date)
                          .startOf('day')
                          .add(Math.abs(minutes), 'minutes')
                          .format('hh:mm A')}
                      </Button>
                      {!vendorView && (
                        <div>
                          {size(getSlotDeliveries(timeSlot, deliveries))}
                        </div>
                      )}
                      {!vendorView && (
                        <div>
                          {getSlotUnits(timeSlot, deliveries).toString()}
                        </div>
                      )}
                      {!vendorView && (
                        <div>
                          {getSlotBoxes(timeSlot, deliveries).toString()}
                        </div>
                      )}
                    </Paper>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className={styles.timeSlots}>
              {loading || (isEmpty(datesAvailabilities) && !vendorView) ? (
                <LoadingText text="Loading Time Slots" />
              ) : (
                <div>
                  There are no available timeslots for this date. Please select
                  a different date.
                  <br />
                  If you require this specific drop-off date, please contact{' '}
                  <a href="mailto:receiving@fashionnova.com">
                    receiving@fashionnova.com
                  </a>
                  , and we can assist you.
                </div>
              )}
            </div>
          )}

          <div className={styles.stepActions}>
            <Button
              onClick={back}
              color="primary"
              className={styles.backButton}
              variant="outlined"
            >
              Back
            </Button>
            <Button
              onClick={onClose}
              variant="text"
              className={styles.cancelButton}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              disabled={!this.state.timeSlot}
              onClick={next}
              variant="contained"
              color="white"
              className={styles.nextButton}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

SelectTimeSlot.propTypes = {
  availabilities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  back: PropTypes.func.isRequired,
  defaultDate: PropTypes.instanceOf(moment),
  deliveries: PropTypes.shape({}),
  getDeliveries: PropTypes.func,
  deliveryDetails: PropTypes.shape({
    timeSlot: PropTypes.instanceOf(Date),
    purchaseOrders: PropTypes.shape({}).isRequired,
    location_id: PropTypes.string.isRequired,
    boxes: PropTypes.number.isRequired,
    units: PropTypes.number.isRequired,
  }).isRequired,
  getAvailabilities: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  location: PropTypes.shape({}),
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  next: PropTypes.func.isRequired,
  vendorView: PropTypes.bool,
};

SelectTimeSlot.contextType = FeatureFlagsContext;

SelectTimeSlot.defaultProps = {
  defaultDate: getDeltaDate(2),
  deliveries: {},
  getDeliveries: () => {},
  location: {
    min_booking_days: 2,
    max_booking_days: 10,
  },
  vendorView: false,
};

export default SelectTimeSlot;
